/**
 * Utilitários compartilhados para a feature de Relatórios de Entregas (REEDY-16).
 * Usados pelo hook useDeliveriesReport e pela página DeliveriesReportPage.
 */

import type { DeliveriesReportRow } from '../services/firebaseFunctions';

/** Dimensão selecionável na UI */
export type ReportDimension = 'company' | 'publisher' | 'combo';

/** Labels de exibição por dimensão (para ToggleButton, selects, etc.) */
export const DIMENSION_LABELS: Record<ReportDimension, string> = {
  company: 'Empresa',
  publisher: 'Editora',
  combo: 'Combo/Plano',
};

/** Headers de coluna por dimensão (para a tabela na UI) */
export const DIMENSION_COLUMN_HEADERS: Record<ReportDimension, string> = {
  company: 'Empresa',
  publisher: 'Editora',
  combo: 'Combo',
};

/**
 * Headers da coluna secundária por dimensão.
 * `null` indica que a dimensão não possui uma única coluna secundária fixa.
 * Para 'combo' isso é intencional: a tabela usa colunas dinâmicas por produto
 * (`hook.productColumns`) seguidas de "Total", renderizadas à parte em
 * DeliveriesReportPage — decisão do PO, 2026-08-07.
 */
export const DIMENSION_SECONDARY_COLUMN_HEADERS: Record<ReportDimension, string | null> = {
  company: 'Produto',
  publisher: 'Título',
  combo: null,
};

/**
 * Busca a quantidade de um produto específico no `productBreakdown` de uma
 * linha do relatório por combo. Retorna 0 quando o combo nunca entregou aquele
 * produto (mantém a célula alinhada com as colunas globais de `productColumns`).
 */
export function getComboProductQuantity(row: DeliveriesReportRow, productId: string): number {
  return row.productBreakdown?.find((p) => p.productId === productId)?.quantity ?? 0;
}

/**
 * Dispara download de um Blob recebido do backend via <a> temporário.
 * Usado pelo hook para o fluxo de exportação CSV (backend gera o CSV, frontend faz o download).
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Monta o nome do arquivo de exportação CSV.
 * Consistente com o Content-Disposition enviado pelo backend.
 */
export function buildCsvFilename(
  dimension: ReportDimension,
  dateStart: string,
  dateEnd: string
): string {
  return `relatorio-entregas-${dimension}-${dateStart}-${dateEnd}.csv`;
}
